import { HttpErrorResponse } from '@angular/common/http';
import { getNumber } from '@base/utils';
import { Component, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter, OnDestroy } from '@angular/core';
import { MatRadioChange } from '@angular/material/radio';
import { MatDialog, MatSnackBar } from '@angular/material';
import { ActivatedRoute } from '@angular/router';
import { TagDialogComponent } from '@tag/tag-dialog/tag-dialog.component';
import { ITag, IWorkbook, IErrorResponse } from '@interfaces';
import { EMPTY } from '@utils';
import { ICompany } from '@company/company.structures';
import { Subscription } from 'rxjs';
import { WorkbookService } from '@base/workbook/workbook.service';
import { ExceptionService } from '@base/exceptions.service';
import { TagService } from '@base/tag/tag.service';
import { SetViewComponent } from '@workbook/setview-dialog/setview.component';
import { Store } from '@ngrx/store';
import { RootState } from '@base/rootReducers';
import * as WorkbookActions from '@workbook/store/workbook.actions';
import { Workbook } from '@models';
import { OpenWorkbookAs } from '@utils';

export interface ITagDialogParams {
  title: string;
  tag: ITag;
  edit: boolean;
  companyId: number;
  workbook: IWorkbook;
  company: ICompany;
}

@Component({
  selector: 'app-workbook-option',
  templateUrl: './workbook-option.component.html',
  styleUrls: ['./workbook-option.component.sass']
})
export class WorkbookOptionComponent implements OnInit, OnDestroy, OnChanges {
  private subscriptions: Subscription[];

  selectable = false;
  removable = true;
  companyTags: ITag[];
  companyId: number;
  gettingTags: boolean;
  openWorkBookAs = {Report: OpenWorkbookAs.Report, Image: OpenWorkbookAs.Image};

  @Input() workbook: IWorkbook = null;
  @Input() company: ICompany = null;
  @Output() toggle = new EventEmitter<Object>();

  constructor(
    private store: Store<RootState>,
    private dialog: MatDialog,
    private workbookService: WorkbookService,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private exceptionService: ExceptionService,
    private tagService: TagService,
  ) {
    this.gettingTags = false;
    this.subscriptions = [];
    this.companyTags = [];
    this.companyId = getNumber(this.route.snapshot.paramMap.get('companyId'));

    this.subscriptions = [...this.subscriptions, ];
  }

  ngOnInit() {
    this.companyTags = this.company.tags;
  }

  ngOnDestroy(): void {
    this.subscriptions.map((s: Subscription) => s.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges) { }

  onToggle() {
    this.toggle.emit(this.workbook);
  }

  private prepareTags(workbook: IWorkbook) {
    const totalTags = workbook.tags.length;
    if (totalTags === 0) {
      this.gettingTags = true;
      this.workbookService.workbooksTags([workbook.id]).subscribe(
        (wbTags: []) => {
          this.gettingTags = false;
          workbook.tags = wbTags;
          this.tagsAdapter(workbook);
        },
        (e: HttpErrorResponse) => { const err: IErrorResponse = this.exceptionService.errorHandling(e); }
      );
    } else if (totalTags > 0) {
      this.tagsAdapter(workbook);
    }
  }

  optionChanged(mrChange: MatRadioChange) {
    // const isChecked = mrChange.source.checked;
    const workbook: IWorkbook = mrChange.value;
    this.companyTags = this.company.tags;
    this.prepareTags(workbook);
  }

  removeTag(tag: ITag) {
    this.workbookService.updateTag(this.workbook, tag, 'remove').subscribe(
      (res) => {
        const auxTags: ITag[] = [...this.workbook.tags];
        const tagPos = auxTags.findIndex(w => w.id === tag.id);
        const deleted = auxTags.splice(tagPos, 1)[0];
        this.workbook.tags = auxTags as [];
        this.companyTags = [...this.companyTags, deleted];
        this.openSnackBar(`La etiqueta ${tag.name} se elimino del Reporte`, 'info');
      },
      (e: HttpErrorResponse) => {const err: IErrorResponse = this.exceptionService.errorHandling(e); }
    );
  }

  openTagDialog() {
    const params: ITagDialogParams = {
      title: 'Agregar Nueva Etiqueta',
      tag: EMPTY,
      edit: false,
      companyId: this.company.id,
      workbook: this.workbook,
      company: this.company,
    };

    const dialogRef = this.dialog.open(TagDialogComponent, {
      // height: '198px',
      width: '370px',
      data: params,
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((tag: ITag) => {
      const validTag = (typeof tag !== 'undefined' && tag !== EMPTY);
      if (validTag) {
        this.workbook.tags = [...this.workbook.tags, tag] as [];
        this.company.tags = [...this.company.tags, tag];
        this.prepareTags(this.workbook);
        this.workbookService.updateTag(this.workbook, tag, 'add').subscribe(
          res => {
            this.openSnackBar(`La etiqueta ${tag.name} fue agregada al Reporte`, 'info');
            this.tagService.addTag(tag);
          },
          (e: HttpErrorResponse) => {const err: IErrorResponse = this.exceptionService.errorHandling(e); }
        );
      }
    });
  }

  private tagsAdapter(workbook: IWorkbook) {
    const auxWorkbookTags = [...workbook.tags];
    const auxCompanyTags = [...this.companyTags];
    const finalTags = auxCompanyTags.filter((coTag: ITag) => {
      const tagExists = auxWorkbookTags.some((wbTag: ITag) => wbTag.id === coTag.id);
      if (!tagExists) {return coTag; }
    });

    this.companyTags = finalTags;
  }

  linkTag(tag: ITag) {
    const auxCompanyTags = [...this.companyTags];
    const itemMoved = auxCompanyTags.splice(auxCompanyTags.findIndex((c: ITag) => c.id === tag.id), 1);
    let workbooksTags: ITag[] = this.workbook.tags;

    const tagExists = workbooksTags.some((w: ITag) => w.id === tag.id);
    if (!tagExists) {
      workbooksTags = [...workbooksTags, tag];

      this.workbookService.updateTag(this.workbook, tag, 'add').subscribe(
        res => {
          this.companyTags = auxCompanyTags;
          this.workbook.tags = workbooksTags as [];
          this.openSnackBar(`La etiqueta ${tag.name} fue agregada al Reporte`, 'info');
        },
        (e: HttpErrorResponse) => {const err: IErrorResponse = this.exceptionService.errorHandling(e); }
      );
    } else {
      this.companyTags = auxCompanyTags;
    }
  }

  private openSnackBar(message: string, action: string): void {
    this.snackBar.open(message, action, {
      verticalPosition: 'top',
      horizontalPosition: 'right',
      duration: 4000,
    });
  }

  openViewsDialog() {
    const wbId = this.workbook.id;
    const auxWorkbook: Workbook = Object.assign(new Workbook(), this.workbook);
    this.store.dispatch(WorkbookActions.setWorkbook({workbook: auxWorkbook}));
    this.store.dispatch(WorkbookActions.getViews({workbookId: wbId}) );
    const params: any = {
      title: 'Agregar Nueva Etiqueta',
      tag: EMPTY,
      edit: false,
      companyId: this.company.id,
      workbook: this.workbook,
      company: this.company,
    };

    const dialogRef = this.dialog.open(SetViewComponent, {
      width: '400px',
      data: params,
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((editedWorkbook) => {
      if (editedWorkbook !== null && typeof editedWorkbook !== 'undefined') {
        this.workbook.open_as = editedWorkbook.open_as;
      }
    });
  }

  syncTableauWorkbook() {
    const wbId = this.workbook.id;
    alert(`sincronizar reporte y vistas ${wbId}`);
  }
}
